import type { EngineWorkscopeLevel, EngineWorkscopeRecord } from '@avisoma/shared';

/**
 * Shared presentation helpers for the engine shop-visit workscope family (DR-3126).
 * The page view, the entity's consolidated card and the project tab all read their
 * labels from here so a level never renders as its raw enum key in one place and a
 * human phrase in another.
 *
 * The evidence lanes the record also carries (`workscopeLevelRaw`, `dateRaw`,
 * `dateLabel`) are deliberately NOT surfaced by these helpers — they exist for
 * auditing and stay visible through the row inspector / debug payload instead of
 * doubling every table column.
 */

const WORKSCOPE_LEVEL_LABELS: Record<EngineWorkscopeLevel, string> = {
  full: 'Full',
  light: 'Light',
  'performance-restoration': 'Performance Restoration',
};

// A Map rather than an indexed lookup on the Record: extraction results reach the UI
// as raw jsonb, so a stored level of `"toString"` would otherwise resolve through
// Object.prototype to a Function and render as its source text.
const LEVEL_LABEL_BY_KEY = new Map<string, string>(Object.entries(WORKSCOPE_LEVEL_LABELS));

/**
 * Human label for a normalised workscope level. An unrecognised value is echoed back
 * verbatim rather than dropped — a newer server deriving a new level is forward
 * compatibility, not corruption.
 */
export function formatWorkscopeLevel(level: string | undefined): string | undefined {
  if (!level) return undefined;
  return LEVEL_LABEL_BY_KEY.get(level) ?? level;
}

/**
 * Comma-joined list, or `undefined` when the page listed none. Guards on
 * `Array.isArray` because the value reaches the UI as raw jsonb.
 */
function formatStringList(values: string[] | undefined): string | undefined {
  if (!Array.isArray(values) || values.length === 0) return undefined;
  return values.join(', ');
}

/** Comma-joined modules, or `undefined` when the page listed none. */
export function formatModulesWorked(modules: string[] | undefined): string | undefined {
  return formatStringList(modules);
}

/** Comma-joined printed work orders (DR-4360), or `undefined` when the page printed none. */
export function formatWorkOrders(workOrders: string[] | undefined): string | undefined {
  return formatStringList(workOrders);
}

/**
 * Engine-header clock as a label value. `0` is a valid TSN/CSN and must render;
 * omitted clocks stay off the card.
 */
export function formatWorkscopeClock(value: number | undefined): string | undefined {
  if (value == null) return undefined;
  return String(value);
}

/**
 * The record's present scalars as label/value pairs, in reading order. Absent fields
 * are dropped so an empty-ish record renders as a short card rather than a grid of
 * em dashes.
 */
export function presentWorkscopeFields(record: EngineWorkscopeRecord): [string, string][] {
  const fields: [string, string | undefined][] = [
    ['Shop Visit Ref', record.shopVisitRef],
    ['Work Orders', formatWorkOrders(record.workOrders)],
    ['Workscope Level', formatWorkscopeLevel(record.workscopeLevel)],
    ['Modules Worked', formatModulesWorked(record.modulesWorked)],
    ['Date', record.date],
    ['TSN', formatWorkscopeClock(record.tsn)],
    ['CSN', formatWorkscopeClock(record.csn)],
    ['TSLSV', formatWorkscopeClock(record.tslsv)],
    ['CSLSV', formatWorkscopeClock(record.cslsv)],
    ['Facility', record.facility],
  ];

  return fields.filter((entry): entry is [string, string] => entry[1] != null && entry[1] !== '');
}
